// The agent draft's English for Translate (2026-10-20, prod contact 7891226).
//
// App.jsx fetches the conversation's translations once, when Translate is
// clicked. The agent draft lands seconds after the suggestion, and a
// regeneration makes a new suggestion, so that cached copy often has no agent
// draft, or one for an older suggestion, and shadowTranslationFor() hides it.
// This fetches just the agent draft's translation (POST
// /api/conversations/:id/shadow-draft-translation, admin-only, cached on its
// row) whenever Translate is on, the current suggestion's agent draft exists,
// and nothing cached matches it: when the draft arrives, and when the
// suggestion is regenerated with Translate on. One result per suggestionId,
// so turning Translate off and on refetches nothing. Messages and the main
// draft are never refetched.
//
// Returns { translation, status }: status 'loading' | 'error' | 'ok' | null.
import { useEffect, useRef, useState } from 'react'
import { fetchShadowDraftTranslation } from './api.js'

export function useShadowTranslation({ conversationId, draft, showTranslation, cached = null, enabled = true }) {
  const [bySuggestion, setBySuggestion] = useState({})
  const requested = useRef(new Set())
  const sid = draft?.suggestionId ?? null
  const cachedMatches = Boolean(cached && sid && cached.suggestionId === sid)

  // A different contact starts over.
  useEffect(() => {
    setBySuggestion({})
    requested.current = new Set()
  }, [conversationId])

  useEffect(() => {
    if (!enabled || !showTranslation || !conversationId || !sid || cachedMatches) return
    if (requested.current.has(sid)) return
    requested.current.add(sid)
    setBySuggestion((m) => ({ ...m, [sid]: { status: 'loading', data: null } }))
    fetchShadowDraftTranslation(conversationId)
      .then((r) => {
        const d = r?.shadowDraft ?? null
        setBySuggestion((m) => ({ ...m, [sid]: { status: 'ok', data: d && d.suggestionId === sid ? d : null } }))
      })
      .catch(() => setBySuggestion((m) => ({ ...m, [sid]: { status: 'error', data: null } })))
  }, [enabled, showTranslation, conversationId, sid, cachedMatches])

  if (cachedMatches) return { translation: cached, status: 'ok' }
  const own = sid ? bySuggestion[sid] : null
  return { translation: own?.data ?? null, status: own?.status ?? null }
}
